// ZPL Template for Zebra ZD421 with 2x4 inch labels
// Resolution: 203 DPI
// Label size: 2" x 4" (406 x 812 dots)

export interface LabelData {
  orderId: string;
  customerId: string;
  partNumber: string;
  description: string;
  poNumber: string;
  date: string;
  itemId?: number;
}

// Zebra ZD421 configuration
export const ZEBRA_CONFIG = {
  dpi: 203,
  labelWidth: 2, // inches
  labelHeight: 4, // inches
  labelWidthDots: 406, // 2 * 203
  labelHeightDots: 812, // 4 * 203
};

// Generate ZPL code for a single label
export function generateZebraLabelZpl(data: LabelData): string {
  // Escape special ZPL characters
  const escape = (text: string): string => {
    return text
      .replace(/\\/g, '\\\\')
      .replace(/\^/g, '\\^')
      .replace(/~/g, '\\~');
  };

  // Truncate description if too long (max ~50 chars per line, 2 lines)
  const truncateDescription = (desc: string, maxLength: number = 100): string => {
    if (desc.length <= maxLength) return desc;
    return desc.substring(0, maxLength - 3) + '...';
  };

  const orderId = escape(data.orderId);
  const customerId = escape(data.customerId);
  const partNumber = escape(data.partNumber);
  const description = escape(truncateDescription(data.description));
  const poNumber = escape(data.poNumber);
  const date = escape(data.date);

  // Generate barcode data (order ID for scanning)
  const barcodeData = escape(data.orderId.replace(/-/g, ''));

  // ZPL Template for 2x4" label on Zebra ZD421
  // ^XA = Start format
  // ^PW406 = Print width (406 dots for 2")
  // ^LL812 = Label length (812 dots for 4")
  // ^LH0,0 = Label home position
  // ^FO = Field origin (x,y position)
  // ^FD = Field data
  // ^FS = Field separator
  // ^A0 = Scalable font
  // ^BY = Barcode defaults
  // ^BC = Code 128 barcode
  // ^XZ = End format

  return `^XA
^PW406
^LL812
^LH0,0
^CI28

~SD20
^PR4,4

^FO0,60
^A0N,45,40
^FB406,1,0,C,0
^FD${orderId}^FS

^FO0,115
^A0N,28,24
^FB406,1,0,C,0
^FDCUST: ${customerId}^FS

^FO20,155
^GB366,2,2^FS

^FO0,170
^A0N,32,28
^FB406,1,0,C,0
^FDPART#^FS

^FO0,210
^A0N,36,32
^FB406,1,0,C,0
^FD${partNumber}^FS

^FO20,260
^GB366,2,2^FS

^FO0,275
^A0N,24,20
^FB406,1,0,C,0
^FDDESCRIPTION:^FS

^FO0,305
^A0N,26,22
^FB406,3,0,C,0
^FD${description}^FS

^FO20,400
^GB366,2,2^FS

^FO0,415
^A0N,28,24
^FB406,1,0,C,0
^FDPO#: ${poNumber}^FS

^FO0,455
^A0N,26,22
^FB406,1,0,C,0
^FDDATE: ${date}^FS

^FO20,510
^GB366,2,2^FS

^FO53,540
^BY2,2,80
^BCN,80,Y,N,N
^FD${barcodeData}^FS

^FO0,660
^A0N,20,18
^FB406,1,0,C,0
^FDScan for tracking^FS

^XZ`;
}

// Generate ZPL for multiple copies
export function generateZebraLabelsZpl(data: LabelData, copies: number = 1): string {
  let zpl = '';
  for (let i = 0; i < copies; i++) {
    zpl += generateZebraLabelZpl(data);
  }
  return zpl;
}

// Print darkness levels (1-30, default 20)
export function setDarkness(level: number): string {
  const darkness = Math.max(1, Math.min(30, level));
  return `~SD${darkness}`;
}

// Print speed (2-6 for ZD421, in inches per second)
export function setSpeed(ips: number): string {
  const speed = Math.max(2, Math.min(6, ips));
  return `^PR${speed},${speed}`;
}

// Calibrate printer (run after loading new label stock)
export function getCalibrationZpl(): string {
  return `^XA
^JUS
~JC
^XZ`;
}

// Test print pattern
export function getTestLabelZpl(): string {
  return `^XA
^PW406
^LL812
^LH0,0

^FO20,20
^A0N,40,35
^FDZebra ZD421 Test^FS

^FO20,80
^A0N,28,24
^FDLabel Size: 2" x 4"^FS

^FO20,120
^A0N,28,24
^FDResolution: 203 DPI^FS

^FO20,180
^GB366,2,2^FS

^FO20,200
^A0N,24,20
^FDBarcode Test:^FS

^FO50,240
^BY2,2,60
^BCN,60,Y,N,N
^FDTEST123456^FS

^FO20,350
^GB366,100,2^FS

^FO30,360
^A0N,20,18
^FDThis is a test label^FS
^FO30,385
^A0N,20,18
^FDfor the CalTrend Atlas^FS
^FO30,410
^A0N,20,18
^FDproduction system.^FS

^FO20,480
^GB366,2,2^FS

^FO100,500
^GC60,3,B^FS

^FO200,500
^GD60,60,3,B,R^FS

^FO100,600
^GE80,40,3,B^FS

^FO250,600
^A0N,24,20
^FDShapes^FS

^FO20,700
^GB366,2,2^FS

^FO100,720
^A0N,32,28
^FD* SUCCESS *^FS

^FO20,770
^A0N,18,16
^FDPrinter configured correctly^FS

^XZ`;
}
